import React from 'react';
import { 
  Box, Typography, Table, TableHead, TableRow, TableCell, TableBody, Paper, 
  TableContainer, Button, Dialog, DialogTitle, DialogContent, DialogActions, 
  Tooltip, IconButton, TextField, Select, MenuItem, FormControl, InputLabel,
  Accordion, AccordionSummary, AccordionDetails, Alert
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { Delete, Edit, Add, History, Business, Check, Close, List, ExpandMore } from '@mui/icons-material';
import { toast } from "react-toastify";
import { useTranslation } from 'react-i18next';
import Loader from 'UI/Application/Components/Loaders/Loader';
import DialogLoader from 'UI/Application/Components/Loaders/DialogLoader';
import { 
  fetchExtraTypeConfigList, 
  deleteExtraTypeConfig, 
  createExtraType,
  updateExtraType,
  deleteExtraType,
  fetchExtraTypeList
} from '../../Domain/Services/ExtraTypeConfigService';
import { PRIVATE_ADMIN_EXTRA_CONFIG_FORM } from '../Constants/PathsConstant';
import { ModernPageHeader, ModernTableContainer } from 'UI/Application/Components/ModernLayout';
import { useAuthContext } from 'Shared/Security/Domain/Contexts/authContext';

function ExtraTypeConfigListView() {
  const { t } = useTranslation();
  const { isAdmin, checkPermission } = useAuthContext();
  const hasAccess = isAdmin() || checkPermission('EXTRAS');
  const [loading, setLoading] = React.useState(true);
  const [sendingRequest, setSendingRequest] = React.useState(false);
  const [configs, setConfigs] = React.useState([]);
  const [openDeleteModal, setOpenDeleteModal] = React.useState(false);
  const [selectedId, setSelectedId] = React.useState(null);
  const [openHistoryModal, setOpenHistoryModal] = React.useState(false);
  const [historyEntries, setHistoryEntries] = React.useState([]);
  const [historyTitle, setHistoryTitle] = React.useState('');
  const [openDelegationModal, setOpenDelegationModal] = React.useState(false);
  const [selectedExtraType, setSelectedExtraType] = React.useState(null);
  
  // States for managing ExtraTypes (CRUD)
  const [openManageTypesModal, setOpenManageTypesModal] = React.useState(false);
  const [allExtraTypes, setAllExtraTypes] = React.useState([]);
  const [newTypeName, setNewTypeName] = React.useState('');
  const [newTypeClassName, setNewTypeClassName] = React.useState('');
  const [filterClassName, setFilterClassName] = React.useState('all');
  const [editingTypeId, setEditingTypeId] = React.useState(null);
  const [editingName, setEditingName] = React.useState('');
  const [editingClassName, setEditingClassName] = React.useState('');
  const [openDeleteTypeModal, setOpenDeleteTypeModal] = React.useState(false);
  const [selectedDeleteTypeId, setSelectedDeleteTypeId] = React.useState(null);

  const loadData = React.useCallback(async () => {
    setLoading(true);
    try {
      const [configsData, typesData] = await Promise.all([
        fetchExtraTypeConfigList(),
        fetchExtraTypeList()
      ]);
      setConfigs(configsData);
      setAllExtraTypes(typesData);
    } catch (error) {
      toast.error("Error al cargar los datos de configuración");
    } finally {
      setLoading(false);
    }
  }, []);

  const handleAddTypeSubmit = async () => {
    if (!newTypeName.trim()) {
      toast.error("El nombre no puede estar vacío");
      return;
    }
    if (!newTypeClassName) {
      toast.error("Debes seleccionar un tipo de clase/validación");
      return;
    }
    setSendingRequest(true);
    try {
      await createExtraType({
        name: newTypeName.trim(),
        className: newTypeClassName,
      });
      toast.success(t('extra.afterSalesExtra.config.messages.successCreateType'));
      setNewTypeName('');
      setNewTypeClassName('');
      await loadData();
    } catch (error) {
      toast.error(error.message || "Error al crear el tipo de extra");
    } finally {
      setSendingRequest(false);
    }
  };

  const handleUpdateTypeSubmit = async (id) => {
    if (!editingName.trim()) {
      toast.error("El nombre no puede estar vacío");
      return;
    }
    setSendingRequest(true);
    try {
      await updateExtraType(id, {
        name: editingName.trim(),
        className: editingClassName,
      });
      toast.success(t('extra.afterSalesExtra.config.messages.successUpdateType'));
      setEditingTypeId(null);
      await loadData();
    } catch (error) {
      toast.error(error.message || "Error al actualizar el tipo de extra");
    } finally {
      setSendingRequest(false);
    }
  };

  const handleDeleteTypeClick = (id) => {
    setSelectedDeleteTypeId(id);
    setOpenDeleteTypeModal(true);
  };

  const handleConfirmDeleteType = async () => {
    if (!selectedDeleteTypeId) return;
    setSendingRequest(true);
    try {
      await deleteExtraType(selectedDeleteTypeId);
      toast.success(t('extra.afterSalesExtra.config.messages.successDeleteType'));
      setOpenDeleteTypeModal(false);
      await loadData();
    } catch (error) {
      toast.error(error.message || "Error al eliminar el tipo de extra. Es posible que esté asociado a registros existentes.");
    } finally {
      setSendingRequest(false);
      setSelectedDeleteTypeId(null);
    }
  };

  const getFriendlyClassName = (className) => {
    switch (className) {
      case 'App\\Extra\\AfterSalesExtra\\Domain\\Entity\\AfterSalesFestiveDayExtra':
        return 'Extra Tipo Festivo';
      case 'App\\Extra\\AfterSalesExtra\\Domain\\Entity\\AfterSalesPreventiveExtra':
        return 'Extra Tipo Preventivo';
      case 'App\\Extra\\AfterSalesExtra\\Domain\\Entity\\AfterSalesWarningExtra':
        return 'Extra Tipo Aviso';
      case 'App\\Extra\\AfterSalesExtra\\Domain\\Entity\\AfterSalesExtra':
        return 'Extra Tipo Genérico';
      default:
        return 'Extra Tipo Genérico';
    }
  };

  const uniqueExtraTypes = React.useMemo(() => {
    const seen = new Set();
    return configs
      .filter((config) => {
        if (seen.has(config.extraTypeId)) return false;
        seen.add(config.extraTypeId);
        return true;
      })
      .sort((a, b) => (a.extraTypeName || '').localeCompare(b.extraTypeName || ''));
  }, [configs]);

  const filteredExtraTypes = React.useMemo(() => {
    if (filterClassName === 'all') {
      return allExtraTypes;
    }
    return allExtraTypes.filter(type => type.className === filterClassName);
  }, [allExtraTypes, filterClassName]);

  const delegationEntries = React.useMemo(() => {
    if (!selectedExtraType) return [];
    
    // Filtramos por tipo de extra
    const filtered = configs.filter((config) => config.extraTypeId === selectedExtraType.extraTypeId);
    
    // Agrupamos por delegación y nos quedamos solo con la más reciente
    const latestByDelegation = new Map();
    filtered.forEach(config => {
      const existing = latestByDelegation.get(config.delegationId);
      if (!existing || new Date(config.startDate) > new Date(existing.startDate)) {
        latestByDelegation.set(config.delegationId, config);
      }
    });

    // Convertimos a array y ordenamos alfabéticamente por nombre de delegación
    return Array.from(latestByDelegation.values())
      .sort((a, b) => (a.delegationName || '').localeCompare(b.delegationName || ''));
  }, [selectedExtraType, configs]);

  React.useEffect(() => {
    if (hasAccess) {
      loadData();
    }
  }, [hasAccess, loadData]);

  const handleDeleteClick = (id) => {
    setSelectedId(id);
    setOpenDeleteModal(true);
  };

  const handleHistoryClick = (config) => {
    const entries = configs
      .filter((item) => item.extraTypeId === config.extraTypeId && item.delegationId === config.delegationId)
      .sort((a, b) => new Date(a.startDate) - new Date(b.startDate));

    setHistoryEntries(entries);
    setHistoryTitle(`${config.extraTypeName} · ${config.delegationName}`);
    setOpenHistoryModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!selectedId) return;
    setSendingRequest(true);
    try {
      await deleteExtraTypeConfig(selectedId);
      toast.success(t('extra.afterSalesExtra.config.messages.successDelete'));
      setOpenDeleteModal(false);
      loadData();
    } catch (error) {
      toast.error("Error al eliminar la configuración");
    } finally {
      setSendingRequest(false);
      setSelectedId(null);
    }
  };

  const handleOpenDelegationModal = (extraType) => {
    setSelectedExtraType(extraType);
    setOpenDelegationModal(true);
  };

  if (!hasAccess) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Alert severity="error" sx={{ maxWidth: 600, mx: 'auto', borderRadius: 2 }}>
          No tienes permisos para ver o modificar la configuración de extras.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <ModernPageHeader
        tag="Configuración"
        title={t('extra.afterSalesExtra.config.title')}
        actions={
          <>
            <Button
              variant="outlined"
              size="small"
              startIcon={<List />}
              onClick={() => setOpenManageTypesModal(true)}
              sx={{ borderRadius: '50px', textTransform: 'none', px: 2.5 }}
            >
              {t('extra.afterSalesExtra.config.viewTypes')}
            </Button>
            <Button
              variant="contained"
              size="small"
              startIcon={<Add />}
              component={RouterLink}
              to={`${PRIVATE_ADMIN_EXTRA_CONFIG_FORM}/new`}
              sx={{ borderRadius: '50px', textTransform: 'none', px: 2.5 }}
            >
              {t('extra.afterSalesExtra.config.add')}
            </Button>
          </>
        }
      />

      <ModernTableContainer
        title="Tipos de Extra"
        count={uniqueExtraTypes.length}
        countLabel="tipos configurados"
        sx={{ display: 'block' }}
      >
        <Table sx={{ minWidth: 500 }}>
          <TableHead sx={{ bgcolor: 'grey.50' }}>
            <TableRow>
              <TableCell align="center" sx={{ fontWeight: 'bold' }}>{t('extra.afterSalesExtra.config.fields.extraType')}</TableCell>
              <TableCell align="center" sx={{ fontWeight: 'bold' }}>Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={2} align="center" sx={{ py: 5 }}>
                  <Loader />
                </TableCell>
              </TableRow>
            ) : uniqueExtraTypes.length > 0 ? (
              uniqueExtraTypes.map((extraType) => (
                <TableRow key={extraType.id} sx={{ '&:hover': { bgcolor: 'action.hover' } }}>
                  <TableCell align="center">{extraType.extraTypeName}</TableCell>
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                      <Tooltip title="Delegaciones">
                        <IconButton 
                          color="primary" 
                          onClick={() => handleOpenDelegationModal(extraType)}
                          size="small"
                          sx={{ 
                            border: '1px solid', 
                            borderColor: 'primary.light',
                            '&:hover': { bgcolor: 'primary.main', color: 'white' }
                          }}
                        >
                          <Business />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={2} align="center" sx={{ py: 10 }}>
                  <Typography variant="body1" color="text.secondary">
                    No hay configuraciones registradas.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </ModernTableContainer>

      <Dialog open={openDeleteModal} onClose={() => setOpenDeleteModal(false)}>
        <DialogTitle>{t('extra.afterSalesExtra.config.delete')}</DialogTitle>
        <DialogContent>
          <Typography>{t('extra.afterSalesExtra.config.messages.confirmDelete')}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteModal(false)}>Cancelar</Button>
          <Button variant="contained" color="error" onClick={handleConfirmDelete}>Eliminar</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openDelegationModal} onClose={() => setOpenDelegationModal(false)} fullWidth maxWidth="lg">
        <DialogTitle>Delegaciones - {selectedExtraType?.extraTypeName}</DialogTitle>
        <DialogContent dividers>
          {delegationEntries.length > 0 ? (
            <TableContainer component={Paper} sx={{ borderRadius: 1, boxShadow: 'none' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell align="center" sx={{ fontWeight: 'bold' }}>Delegación</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold' }}>Fecha Inicio</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold' }}>Importe</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                      Acciones
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {delegationEntries.map((entry) => (
                    <TableRow key={entry.id}>
                      <TableCell align="center">{entry.delegationName}</TableCell>
                      <TableCell align="center">{entry.startDate?.split(' ')[0]}</TableCell>
                      <TableCell align="center">{entry.amount} €</TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                          <Tooltip title="Histórico">
                            <IconButton color="info" onClick={() => handleHistoryClick(entry)} size="small">
                              <History />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={t('extra.afterSalesExtra.config.edit')}>
                            <IconButton 
                              color="primary" 
                              component={RouterLink} 
                              to={`${PRIVATE_ADMIN_EXTRA_CONFIG_FORM}/${entry.id}`}
                              size="small"
                            >
                              <Edit />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={t('extra.afterSalesExtra.config.delete')}>
                            <IconButton color="error" onClick={() => handleDeleteClick(entry.id)} size="small">
                              <Delete />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Typography>No hay delegaciones para este tipo de extra.</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDelegationModal(false)}>Cerrar</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openHistoryModal} onClose={() => setOpenHistoryModal(false)} fullWidth maxWidth="md">
        <DialogTitle>Histórico - {historyTitle}</DialogTitle>
        <DialogContent dividers>
          {historyEntries.length > 0 ? (
            <TableContainer component={Paper} sx={{ borderRadius: 1, boxShadow: 'none' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell align="center" sx={{ fontWeight: 'bold' }}>Fecha Inicio</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold' }}>Fecha Fin</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold' }}>Importe</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {historyEntries.map((entry) => (
                    <TableRow key={entry.id}>
                      <TableCell align="center">{entry.startDate?.split(' ')[0]}</TableCell>
                      <TableCell align="center">
                        {entry.endDate && entry.endDate.startsWith('9999-12-31') ? '-' : (entry.endDate?.split(' ')[0] || '-')}
                      </TableCell>
                      <TableCell align="center">{entry.amount} €</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Typography>No hay histórico para esta combinación.</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenHistoryModal(false)}>Cerrar</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openManageTypesModal} onClose={() => setOpenManageTypesModal(false)} fullWidth maxWidth="md">
        <DialogTitle>{t('extra.afterSalesExtra.config.modalManageTypesTitle')}</DialogTitle>
        <DialogContent dividers>
          {/* Formulario de creación superior en Acordeón */}
          <Accordion sx={{ mb: 3, border: '1px solid', borderColor: 'divider', boxShadow: 'none', '&:before': { display: 'none' } }}>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
                <Add color="primary" fontSize="small" />
                {t('extra.afterSalesExtra.config.modalAddTypeTitle')}
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ bgcolor: 'grey.50', p: 2, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
              <TextField
                label={t('extra.afterSalesExtra.config.fields.extraType')}
                variant="outlined"
                size="small"
                sx={{ flexGrow: 1, minWidth: '200px', bgcolor: 'white' }}
                value={newTypeName}
                onChange={(e) => setNewTypeName(e.target.value)}
                placeholder="Ej. Plus día Festivo Especial"
              />
              <FormControl size="small" sx={{ minWidth: '220px', bgcolor: 'white' }}>
                <InputLabel id="class-name-select-label">{t('extra.afterSalesExtra.config.fields.className')}</InputLabel>
                <Select
                  labelId="class-name-select-label"
                  id="class-name-select"
                  value={newTypeClassName}
                  label={t('extra.afterSalesExtra.config.fields.className')}
                  onChange={(e) => setNewTypeClassName(e.target.value)}
                >
                  <MenuItem value="">
                    <em>Selecciona una clase...</em>
                  </MenuItem>
                  <MenuItem value={"App\\Extra\\AfterSalesExtra\\Domain\\Entity\\AfterSalesFestiveDayExtra"}>
                    Extra Tipo Festivo
                  </MenuItem>
                  <MenuItem value={"App\\Extra\\AfterSalesExtra\\Domain\\Entity\\AfterSalesPreventiveExtra"}>
                    Extra Tipo Preventivo
                  </MenuItem>
                  <MenuItem value={"App\\Extra\\AfterSalesExtra\\Domain\\Entity\\AfterSalesWarningExtra"}>
                    Extra Tipo Aviso
                  </MenuItem>
                  <MenuItem value={"App\\Extra\\AfterSalesExtra\\Domain\\Entity\\AfterSalesExtra"}>
                    Extra Tipo Genérico
                  </MenuItem>
                </Select>
              </FormControl>
              <Button 
                variant="contained" 
                onClick={handleAddTypeSubmit}
                startIcon={<Add />}
                sx={{ textTransform: 'none', borderRadius: 1.5, px: 3, height: '40px' }}
              >
                Agregar
              </Button>
            </AccordionDetails>
          </Accordion>

          {/* Filtro de clase/validación */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
            <FormControl size="small" sx={{ minWidth: '220px' }}>
              <InputLabel id="filter-class-name-select-label">Filtrar por Clase</InputLabel>
              <Select
                labelId="filter-class-name-select-label"
                id="filter-class-name-select"
                value={filterClassName}
                label="Filtrar por Clase"
                onChange={(e) => setFilterClassName(e.target.value)}
              >
                <MenuItem value="all">
                  <em>Todas las clases</em>
                </MenuItem>
                <MenuItem value={"App\\Extra\\AfterSalesExtra\\Domain\\Entity\\AfterSalesFestiveDayExtra"}>
                  Extra Tipo Festivo
                </MenuItem>
                <MenuItem value={"App\\Extra\\AfterSalesExtra\\Domain\\Entity\\AfterSalesPreventiveExtra"}>
                  Extra Tipo Preventivo
                </MenuItem>
                <MenuItem value={"App\\Extra\\AfterSalesExtra\\Domain\\Entity\\AfterSalesWarningExtra"}>
                  Extra Tipo Aviso
                </MenuItem>
                <MenuItem value={"App\\Extra\\AfterSalesExtra\\Domain\\Entity\\AfterSalesExtra"}>
                  Extra Tipo Genérico
                </MenuItem>
              </Select>
            </FormControl>
          </Box>

          {/* Tabla de gestión interactiva */}
          <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
            <Table size="small">
              <TableHead sx={{ bgcolor: 'grey.50' }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>Nombre</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Tipo de Clase/Validación</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold', width: '120px' }}>Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredExtraTypes.length > 0 ? (
                  filteredExtraTypes.map((type) => {
                    const isEditing = editingTypeId === type.id;
                    return (
                      <TableRow key={type.id} sx={{ '&:hover': { bgcolor: 'action.hover' } }}>
                        {isEditing ? (
                          <>
                            <TableCell>
                              <TextField
                                variant="outlined"
                                size="small"
                                fullWidth
                                value={editingName}
                                onChange={(e) => setEditingName(e.target.value)}
                              />
                            </TableCell>
                            <TableCell>
                              <FormControl size="small" fullWidth>
                                <Select
                                  value={editingClassName}
                                  onChange={(e) => setEditingClassName(e.target.value)}
                                >
                                  <MenuItem value={"App\\Extra\\AfterSalesExtra\\Domain\\Entity\\AfterSalesFestiveDayExtra"}>
                                    Extra Tipo Festivo
                                  </MenuItem>
                                  <MenuItem value={"App\\Extra\\AfterSalesExtra\\Domain\\Entity\\AfterSalesPreventiveExtra"}>
                                    Extra Tipo Preventivo
                                  </MenuItem>
                                  <MenuItem value={"App\\Extra\\AfterSalesExtra\\Domain\\Entity\\AfterSalesWarningExtra"}>
                                    Extra Tipo Aviso
                                  </MenuItem>
                                  <MenuItem value={"App\\Extra\\AfterSalesExtra\\Domain\\Entity\\AfterSalesExtra"}>
                                    Extra Tipo Genérico
                                  </MenuItem>
                                </Select>
                              </FormControl>
                            </TableCell>
                            <TableCell align="center">
                              <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                                <Tooltip title="Guardar">
                                  <IconButton 
                                    color="success" 
                                    size="small"
                                    onClick={() => handleUpdateTypeSubmit(type.id)}
                                  >
                                    <Check />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Cancelar">
                                  <IconButton 
                                    color="error" 
                                    size="small"
                                    onClick={() => setEditingTypeId(null)}
                                  >
                                    <Close />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            </TableCell>
                          </>
                        ) : (
                          <>
                            <TableCell sx={{ fontWeight: 'medium' }}>{type.name}</TableCell>
                            <TableCell>{getFriendlyClassName(type.className)}</TableCell>
                            <TableCell align="center">
                              <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                                <Tooltip title="Editar tipo">
                                  <IconButton 
                                    color="primary" 
                                    size="small"
                                    onClick={() => {
                                      setEditingTypeId(type.id);
                                      setEditingName(type.name);
                                      setEditingClassName(type.className);
                                    }}
                                  >
                                    <Edit />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Eliminar tipo">
                                  <IconButton 
                                    color="error" 
                                    size="small"
                                    onClick={() => handleDeleteTypeClick(type.id)}
                                  >
                                    <Delete />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            </TableCell>
                          </>
                        )}
                      </TableRow>
                    );
                  })
                ) : (
                  <TableRow>
                    <TableCell colSpan={3} align="center" sx={{ py: 3 }}>
                      <Typography variant="body2" color="text.secondary">
                        No hay tipos de extra que coincidan con el filtro.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenManageTypesModal(false)} variant="contained" color="inherit">
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de confirmación de eliminación con diseño premium */}
      <Dialog 
        open={openDeleteTypeModal} 
        onClose={() => setOpenDeleteTypeModal(false)}
        PaperProps={{ sx: { borderRadius: 3, p: 1 } }}
      >
        <DialogTitle sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
          <Delete color="error" />
          {t('extra.afterSalesExtra.config.delete')}
        </DialogTitle>
        <DialogContent>
          <Typography>
            {t('extra.afterSalesExtra.config.messages.confirmDeleteType', '¿Estás seguro de que deseas eliminar este tipo de extra? Esta acción no se puede deshacer.')}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setOpenDeleteTypeModal(false)} variant="outlined" color="inherit" sx={{ borderRadius: 2, textTransform: 'none' }}>
            Cancelar
          </Button>
          <Button variant="contained" color="error" onClick={handleConfirmDeleteType} sx={{ borderRadius: 2, textTransform: 'none', px: 3 }}>
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>

      <DialogLoader open={sendingRequest} />
    </Box>
  );
}

export default ExtraTypeConfigListView;
